import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { useAuthContext } from '@contexts/AuthContext';
import { ViewPageHeader, ResourcePreview, StatusBadge, Card } from '@components/common';
import StateSelector from '@components/common/StateSelector';
import { BorrowingActions, BorrowingProperties, BorrowingNotes, BorrowingTimeline, BorrowingValidation } from './components';
import { useBorrowingPermissions } from '@hooks/useBorrowingPermissions';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';
import './BorrowingViewPage.scss';

export const BorrowingViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, currentLanguage } = useLanguage();
    const { user } = useAuthContext();
    const notify = useNotification();

    // Permissions pour cet emprunt
    const { permissions: borrowingPermissions, loading: permissionsLoading } = useBorrowingPermissions(id);

    const [borrowing, setBorrowing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [participationLoading, setParticipationLoading] = useState(false);

    // Load borrowing data
    useEffect(() => {
        const loadBorrowing = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/borrowings/${id}`);
                setBorrowing(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to load borrowing:', err);
                setError(t('borrowingView.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        loadBorrowing();
    }, [id, t]);

    const handleBack = () => {
        navigate('/borrowings');
    };

    const handleParticipationChange = async (status) => {
        if (!borrowing || participationLoading) return;

        try {
            setParticipationLoading(true);
            await api.patch(`/borrowings/${borrowing.id}/participation`, { status });

            // Reload borrowing to get updated participants list
            const response = await api.get(`/borrowings/${id}`);
            setBorrowing(response.data);

            notify.success(t('borrowing.participation.statusUpdated'));
        } catch (err) {
            console.error('Failed to update participation:', err);
            notify.error(t('borrowing.participation.statusUpdateFailed'));
        } finally {
            setParticipationLoading(false);
        }
    };

    const handleValidationComplete = async () => {
        // Reload borrowing to get updated validation status
        try {
            const response = await api.get(`/borrowings/${id}`);
            setBorrowing(response.data);
        } catch (err) {
            console.error('Failed to reload borrowing after validation:', err);
        }
    };

    const groupParticipantsByStatus = (participants) => {
        const groups = {
            yes: [],
            maybe: [],
            no: []
        };

        participants.forEach(p => {
            if (groups[p.status]) {
                groups[p.status].push(p);
            }
        });

        return groups;
    };


    if (loading) {
        return (
            <div className="borrowing-view-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !borrowing) {
        return (
            <div className="borrowing-view-page">
                <div className="error-container">
                    <h2>{t('borrowingView.errorTitle')}</h2>
                    <p>{error || t('borrowingView.notFound')}</p>
                    <button onClick={handleBack} className="btn btn-primary">
                        {t('common.backToBorrowings')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="borrowing-view-page">

            {/* Main Content */}
            <div className="borrowing-content">
                {/* Borrowing Details Section */}
                <div className="details-section">
                    <Card className="borrowing-info-card">
                        <div className="borrowing-header">
                            <h1 className="borrowing-title">{
                                borrowing.borrowed_at ? t('borrowingView.borrowingDetails') : t('borrowingView.reservationDetails')
                            }</h1>
                            <StatusBadge model={borrowing} type="borrowing" />
                        </div>

                        {/* Resource Preview Section */}
                        {borrowing.resource && (
                            <div className="resource-section">
                                <ResourcePreview
                                    resource={borrowing.resource}
                                    size="large"
                                    clickable={true}
                                    showDescription={true}
                                    noImageText={t('borrowingView.noImage')}
                                    className="borrowing-resource-preview"
                                />
                            </div>
                        )}

                        {/* Borrowing Actions */}
                        <BorrowingActions
                            borrowing={borrowing}
                            permissions={borrowingPermissions}
                        />

                        {/* Reservation Validation Section */}
                        <BorrowingValidation
                            borrowing={borrowing}
                            permissions={borrowingPermissions}
                            onValidationComplete={handleValidationComplete}
                        />

                        {/* Borrowing Properties */}
                        <BorrowingProperties
                            borrowing={borrowing}
                            currentLanguage={currentLanguage}
                        />

                        {/* Participation Section */}
                        {borrowing.allows_participation && (
                            <div className="participation-section">
                                <h3>{t('borrowing.participation.title')}</h3>

                                <div className="my-participation">
                                    <label>{t('borrowing.participation.myStatus')}</label>
                                    <StateSelector
                                        options={[
                                            {
                                                value: 'yes',
                                                label: t('borrowing.participation.yes'),
                                                color: 'success',
                                                icon: '✅'
                                            },
                                            {
                                                value: 'no',
                                                label: t('borrowing.participation.no'),
                                                color: 'error',
                                                icon: '❌'
                                            },
                                            {
                                                value: 'maybe',
                                                label: t('borrowing.participation.maybe'),
                                                color: 'warning',
                                                icon: '❔'
                                            }
                                        ]}
                                        value={borrowing.my_participation}
                                        onChange={handleParticipationChange}
                                        size="medium"
                                        disabled={participationLoading}
                                        allowEmpty={true}
                                    />
                                </div>

                                {borrowing.participants && borrowing.participants.length > 0 && (
                                    <div className="participants-list">
                                        <h4>{t('borrowing.participation.participants')}</h4>

                                        {(() => {
                                            const grouped = groupParticipantsByStatus(borrowing.participants);
                                            return (
                                                <>
                                                    {grouped.yes.length > 0 && (
                                                        <div className="participants-group">
                                                            <div className="group-header">
                                                                <span className="group-icon">✅</span>
                                                                <span className="group-title">{t('borrowing.participation.participantsYes')}</span>
                                                                <span className="group-count">({grouped.yes.length})</span>
                                                            </div>
                                                            <div className="participants-items">
                                                                {grouped.yes.map(p => (
                                                                    <div key={p.user_id} className="participant-item">
                                                                        <span className="participant-name">{p.name || p.email}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {grouped.maybe.length > 0 && (
                                                        <div className="participants-group">
                                                            <div className="group-header">
                                                                <span className="group-icon">❔</span>
                                                                <span className="group-title">{t('borrowing.participation.participantsMaybe')}</span>
                                                                <span className="group-count">({grouped.maybe.length})</span>
                                                            </div>
                                                            <div className="participants-items">
                                                                {grouped.maybe.map(p => (
                                                                    <div key={p.user_id} className="participant-item">
                                                                        <span className="participant-name">{p.name || p.email}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {grouped.no.length > 0 && (
                                                        <div className="participants-group">
                                                            <div className="group-header">
                                                                <span className="group-icon">❌</span>
                                                                <span className="group-title">{t('borrowing.participation.participantsNo')}</span>
                                                                <span className="group-count">({grouped.no.length})</span>
                                                            </div>
                                                            <div className="participants-items">
                                                                {grouped.no.map(p => (
                                                                    <div key={p.user_id} className="participant-item">
                                                                        <span className="participant-name">{p.name || p.email}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}

                                {(!borrowing.participants || borrowing.participants.length === 0) && (
                                    <p className="no-participants">{t('borrowing.participation.noParticipants')}</p>
                                )}
                            </div>
                        )}
                    </Card>

                    {/* Notes Section */}
                    <Card className="borrowing-notes-timeline-card">
                        <BorrowingNotes borrowing={borrowing} />
                        <BorrowingTimeline
                            borrowing={borrowing}
                            currentLanguage={currentLanguage}
                        />
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default BorrowingViewPage;
